import { motion, useReducedMotion } from 'framer-motion'
import { Target } from 'lucide-react'
import { AnimatedKpiValue } from '@/components/ui/AnimatedKpiValue'
import { formatGoalPercent, type TimeTabView } from '@/components/Dashboard/timeTab.utils'
import {
  formatDashboardCurrency,
  formatDashboardNumber,
} from '@/utils/crmValuesDisplay'
import { formatCurrencyCompact } from '@/utils/format-currency'
import { cn } from '@/utils/cn'

type Props = {
  view: TimeTabView
  valuesVisible?: boolean
  animationDelay?: number
}

export function TimeGoalHeroCard({ view, valuesVisible = false, animationDelay = 0 }: Props) {
  const reduced = useReducedMotion()
  const accent = { bg: '#fef3c7', text: '#d97706', ring: '#fcd34d' }
  const goalPercent = Math.min(view.goalPercent, 100)
  const useCompactAchieved = valuesVisible && view.monthlyAchieved >= 100_000
  const useCompactGoal = valuesVisible && view.monthlyGoal >= 100_000

  return (
    <article className='relative flex h-full min-h-[260px] flex-col overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--bg-card)] p-5 shadow-sm sm:p-6'>
      <div
        className='pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full opacity-40 blur-2xl'
        style={{ backgroundColor: accent.ring }}
        aria-hidden
      />

      <div className='flex items-start justify-between gap-3'>
        <div className='min-w-0'>
          <p className='text-sm font-medium text-text-muted'>Meta vs realizado</p>
          <div className='mt-2'>
            {valuesVisible ? (
              <AnimatedKpiValue
                numericValue={view.monthlyAchieved}
                displayValue={
                  useCompactAchieved
                    ? formatCurrencyCompact(view.monthlyAchieved)
                    : formatDashboardCurrency(view.monthlyAchieved, true)
                }
                format='currency'
                compactCurrency={useCompactAchieved}
                className='text-3xl font-bold tabular-nums tracking-tight text-text-primary sm:text-4xl'
                delay={animationDelay}
              />
            ) : (
              <span className='text-3xl font-bold text-text-primary sm:text-4xl'>••••</span>
            )}
          </div>
          <p className='mt-2 text-sm text-text-muted'>
            {formatDashboardNumber(view.closedDeals, valuesVisible)} deals fechados no período
          </p>
        </div>
        <span
          className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ring-1'
          style={{
            backgroundColor: accent.bg,
            color: accent.text,
            boxShadow: `inset 0 0 0 1px ${accent.ring}`,
          }}
          aria-hidden
        >
          <Target size={18} />
        </span>
      </div>

      <div className='mt-5 space-y-2'>
        <div className='flex items-center justify-between text-sm'>
          <span className='text-text-secondary'>Meta do período</span>
          <span className='font-semibold tabular-nums text-text-primary'>
            {valuesVisible
              ? useCompactGoal
                ? formatCurrencyCompact(view.monthlyGoal)
                : formatDashboardCurrency(view.monthlyGoal, true)
              : '••••'}
          </span>
        </div>
        <div className='h-3 overflow-hidden rounded-full border border-[var(--border)] bg-[var(--bg-dark)]/10'>
          <motion.div
            initial={reduced ? false : { width: 0 }}
            animate={{ width: `${goalPercent}%` }}
            transition={{ duration: 0.45, delay: animationDelay / 1000 }}
            className={cn(
              'h-full rounded-full',
              view.goalPercent >= 100
                ? 'bg-gradient-to-r from-emerald-500 to-emerald-400'
                : 'bg-gradient-to-r from-[var(--brand-primary)] to-indigo-400',
            )}
          />
        </div>
        <p className='text-xs text-text-muted'>
          {formatGoalPercent(view.goalPercent, valuesVisible)} da meta alcançada
        </p>
      </div>

      <div className='mt-auto grid grid-cols-2 gap-2 border-t border-[var(--border)] pt-4'>
        <div className='rounded-xl border border-[var(--border)] bg-[var(--bg-dark)]/10 px-3 py-2.5'>
          <p className='text-[10px] font-medium uppercase tracking-wide text-text-muted'>Closers</p>
          <p className='mt-1 text-lg font-bold tabular-nums text-text-primary'>
            {formatDashboardNumber(view.totalClosers, valuesVisible)}
          </p>
        </div>
        <div className='rounded-xl border border-[var(--border)] bg-[var(--bg-dark)]/10 px-3 py-2.5'>
          <p className='text-[10px] font-medium uppercase tracking-wide text-text-muted'>Ticket médio</p>
          <p className='mt-1 text-lg font-bold tabular-nums text-text-primary'>
            {formatDashboardCurrency(view.avgTicket, valuesVisible)}
          </p>
        </div>
      </div>
    </article>
  )
}
